import { Transaction } from '../types';

interface CategoryPattern {
  keywords: string[];
  category: string;
  confidence: number;
  frequency: number;
}

interface SmartSuggestion {
  category: string;
  confidence: number;
  reason: string;
  isLearned: boolean;
}

export class SmartCategorizationService {
  private patterns: CategoryPattern[] = [];
  private transactionHistory: Transaction[] = [];

  // Patterns prédéfinis pour démarrer
  private defaultPatterns: CategoryPattern[] = [
    // Alimentation
    { keywords: ['carrefour', 'leclerc', 'auchan', 'monoprix', 'casino', 'intermarché'], category: 'Alimentation', confidence: 0.9, frequency: 1 },
    { keywords: ['restaurant', 'mcdo', 'kfc', 'burger', 'pizza', 'café'], category: 'Alimentation', confidence: 0.8, frequency: 1 },
    
    // Transport
    { keywords: ['essence', 'carburant', 'total', 'shell', 'esso', 'bp'], category: 'Transport', confidence: 0.9, frequency: 1 },
    { keywords: ['sncf', 'ratp', 'metro', 'bus', 'taxi', 'uber'], category: 'Transport', confidence: 0.8, frequency: 1 },
    { keywords: ['parking', 'péage', 'autoroute'], category: 'Transport', confidence: 0.7, frequency: 1 },
    
    // Shopping
    { keywords: ['amazon', 'fnac', 'darty', 'boulanger', 'ikea'], category: 'Shopping', confidence: 0.8, frequency: 1 },
    { keywords: ['zara', 'h&m', 'uniqlo', 'nike', 'adidas'], category: 'Vêtements', confidence: 0.8, frequency: 1 },
    
    // Services
    { keywords: ['edf', 'engie', 'gaz', 'électricité'], category: 'Utilities', confidence: 0.9, frequency: 1 },
    { keywords: ['orange', 'free', 'sfr', 'bouygues', 'telephone'], category: 'Utilities', confidence: 0.8, frequency: 1 },
    { keywords: ['netflix', 'spotify', 'youtube', 'disney'], category: 'Divertissement', confidence: 0.8, frequency: 1 },
    
    // Santé
    { keywords: ['pharmacie', 'docteur', 'médecin', 'clinique', 'hopital'], category: 'Santé', confidence: 0.9, frequency: 1 },
    
    // Logement
    { keywords: ['loyer', 'rent', 'immobilier', 'agence'], category: 'Logement', confidence: 0.9, frequency: 1 },
  ];

  constructor() {
    this.patterns = [...this.defaultPatterns];
  }

  // Initialiser avec l'historique des transactions
  init(transactions: Transaction[]) {
    this.transactionHistory = transactions;
    this.learnFromHistory();
  }

  // Apprendre des patterns à partir de l'historique
  private learnFromHistory() {
    // Grouper les transactions par description similaire
    const transactionGroups = this.groupSimilarTransactions();
    
    // Pour chaque groupe, créer un pattern si il y a assez d'exemples
    Object.entries(transactionGroups).forEach(([description, transactions]) => {
      if (transactions.length >= 2) { // Au moins 2 occurrences
        const categories = Array.from(new Set(transactions.map(t => t.category)));
        
        // Si toutes les transactions ont la même catégorie, créer un pattern
        if (categories.length === 1) {
          const keywords = this.extractKeywords(description);
          const existingPattern = this.patterns.find(p => 
            keywords.some(k => p.keywords.includes(k))
          );
          
          if (!existingPattern) {
            this.patterns.push({
              keywords,
              category: categories[0],
              confidence: Math.min(0.8, 0.4 + (transactions.length * 0.1)),
              frequency: transactions.length
            });
          } else {
            // Augmenter la confiance du pattern existant
            existingPattern.frequency += transactions.length;
            existingPattern.confidence = Math.min(0.9, existingPattern.confidence + 0.1);
          }
        }
      }
    });
  }

  // Grouper les transactions avec des descriptions similaires
  private groupSimilarTransactions(): Record<string, Transaction[]> {
    const groups: Record<string, Transaction[]> = {};
    
    this.transactionHistory.forEach(transaction => {
      const cleanDescription = this.cleanDescription(transaction.description);
      const key = this.findSimilarKey(cleanDescription, Object.keys(groups));
      
      if (key) {
        groups[key].push(transaction);
      } else {
        groups[cleanDescription] = [transaction];
      }
    });
    
    return groups;
  }

  // Nettoyer la description pour extraction des mots-clés
  private cleanDescription(description: string): string {
    return description
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Extraire les mots-clés significatifs
  private extractKeywords(description: string): string[] {
    const words = this.cleanDescription(description).split(' ');
    const stopWords = ['le', 'la', 'les', 'de', 'du', 'des', 'un', 'une', 'et', 'ou', 'à', 'ce', 'que', 'pour', 'avec', 'par'];
    
    return words
      .filter(word => word.length > 2 && !stopWords.includes(word))
      .slice(0, 3); // Garder les 3 premiers mots significatifs
  }

  // Trouver une clé similaire existante
  private findSimilarKey(description: string, existingKeys: string[]): string | null {
    const threshold = 0.7;
    
    for (const key of existingKeys) {
      if (this.calculateSimilarity(description, key) > threshold) {
        return key;
      }
    }
    
    return null;
  }

  // Calculer la similarité entre deux chaînes
  private calculateSimilarity(str1: string, str2: string): number {
    const words1 = str1.split(' ');
    const words2 = str2.split(' ');
    
    const intersection = words1.filter(word => words2.includes(word));
    const union = Array.from(new Set([...words1, ...words2]));
    
    return intersection.length / union.length;
  }

  // Suggérer une catégorie pour une nouvelle transaction
  suggestCategory(description: string, amount: number): SmartSuggestion | null {
    const cleanDesc = this.cleanDescription(description);
    const words = cleanDesc.split(' ');
    
    const suggestions: SmartSuggestion[] = [];

    // Parcourir tous les patterns
    this.patterns.forEach(pattern => {
      let score = 0;
      let matchedKeywords = 0;

      // Calculer le score basé sur les mots-clés trouvés
      pattern.keywords.forEach(keyword => {
        if (words.some(word => word.includes(keyword) || keyword.includes(word))) {
          matchedKeywords++;
          score += pattern.confidence;
        }
      });

      // Bonus pour la fréquence d'utilisation
      if (matchedKeywords > 0) {
        const calculatedScore = (score / pattern.keywords.length) * (1 + Math.log(pattern.frequency) / 10);
        
        const suggestion: SmartSuggestion = {
          category: pattern.category,
          confidence: Math.min(calculatedScore, 0.95),
          reason: `Basé sur: ${pattern.keywords.slice(0, 2).join(', ')} (${matchedKeywords} mots-clés)`,
          isLearned: !this.defaultPatterns.includes(pattern)
        };
        
        suggestions.push(suggestion);
      }
    });

    // Trouver la meilleure suggestion
    const bestSuggestion = suggestions
      .filter(s => s.confidence > 0.3)
      .sort((a, b) => b.confidence - a.confidence)[0];

    return bestSuggestion || null;
  }

  // Apprendre d'une nouvelle transaction validée par l'utilisateur
  learnFromTransaction(description: string, category: string) {
    const keywords = this.extractKeywords(description);
    
    // Chercher un pattern existant
    const existingPattern = this.patterns.find(p => 
      p.category === category && 
      keywords.some(k => p.keywords.includes(k))
    );

    if (existingPattern) {
      // Renforcer le pattern existant
      keywords.forEach(keyword => {
        if (!existingPattern.keywords.includes(keyword)) {
          existingPattern.keywords.push(keyword);
        }
      });
      existingPattern.frequency++;
      existingPattern.confidence = Math.min(0.9, existingPattern.confidence + 0.05);
    } else {
      // Créer un nouveau pattern
      this.patterns.push({
        keywords,
        category,
        confidence: 0.6,
        frequency: 1
      });
    }
  }

  // Prédire les prochaines dépenses récurrentes
  predictUpcomingExpenses(): Array<{
    description: string;
    category: string;
    predictedAmount: number;
    confidence: number;
    nextDate: Date;
  }> {
    const predictions: Array<{
      description: string;
      category: string;
      predictedAmount: number;
      confidence: number;
      nextDate: Date;
    }> = [];

    // Grouper les transactions par similarité
    const groups = this.groupSimilarTransactions();
    const currentDate = new Date();

    Object.entries(groups).forEach(([description, transactions]) => {
      if (transactions.length >= 3 && transactions[0].type === 'expense') {
        // Analyser la récurrence
        const dates = transactions.map(t => new Date(t.date)).sort((a, b) => a.getTime() - b.getTime());
        const intervals = [];
        
        for (let i = 1; i < dates.length; i++) {
          const days = Math.round((dates[i].getTime() - dates[i-1].getTime()) / (1000 * 60 * 60 * 24));
          intervals.push(days);
        }

        // Détecter si c'est récurrent (intervalle régulier)
        const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
        const isRecurring = intervals.every(interval => Math.abs(interval - avgInterval) < avgInterval * 0.3);

        if (isRecurring && avgInterval > 7) { // Récurrence d'au moins une semaine
          const lastTransaction = transactions[transactions.length - 1];
          const nextDate = new Date(new Date(lastTransaction.date).getTime() + avgInterval * 24 * 60 * 60 * 1000);
          
          // Si la prochaine occurrence est dans le futur proche
          if (nextDate > currentDate && nextDate <= new Date(currentDate.getTime() + 60 * 24 * 60 * 60 * 1000)) {
            const avgAmount = transactions.reduce((sum, t) => sum + t.amount, 0) / transactions.length;
            
            predictions.push({
              description: lastTransaction.description,
              category: lastTransaction.category,
              predictedAmount: Math.round(avgAmount * 100) / 100,
              confidence: Math.min(0.9, 0.5 + (transactions.length * 0.1)),
              nextDate
            });
          }
        }
      }
    });

    return predictions.sort((a, b) => a.nextDate.getTime() - b.nextDate.getTime());
  }

  // Obtenir les statistiques d'apprentissage
  getStats() {
    const learnedPatterns = this.patterns.filter(p => !this.defaultPatterns.includes(p));
    
    return {
      totalPatterns: this.patterns.length,
      learnedPatterns: learnedPatterns.length,
      defaultPatterns: this.defaultPatterns.length,
      transactionsAnalyzed: this.transactionHistory.length,
      avgConfidence: this.patterns.reduce((sum, p) => sum + p.confidence, 0) / this.patterns.length
    };
  }
}

export const smartCategorization = new SmartCategorizationService();
